import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import Picker from 'emoji-picker-react';
import IconButton from '@material-ui/core/IconButton';
import AccountCircleOutlined from '@material-ui/icons/AccountCircleOutlined';
import InsertEmoticonOutlined from '@material-ui/icons/InsertEmoticonOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import CloseIcon from '@material-ui/icons/Close';

import Poll from '../Poll/Poll';
import QuestionPanel from '../Q&A/QuestionPanel';
import ChatLoginOverlay from '../ChatOverlay/ChatLoginOverlay';
import { useChatLogin } from '../../contexts/ChatLoginContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { DownIcon } from '../QPlayerSVGElement';

import {
	ChatContainer,
	ChatTab,
	ChatTabWrapper,
	EmojiIconContainer,
	Message,
	MessageInput,
	MessageInputContainer,
	MessagesContainer,
	StartOfChat,
	MessageNotification,
	MessageNotificationContent,
	ChatTabAction,
	EngagementPanelsStack,
	ChatNotice,
} from './ChatUpdated.styled';
import { joinClassNames } from '../../util/util';

interface Sender {
	admin: boolean;
	name: string;
}

interface Recipient {
	replyMessageId?: string;
	replyQuestionId?: string;
}

interface MessageData {
	text?: string;
	pinned?: boolean;
}

interface ChatMessage {
	id: string;
	sender: Sender;
	recipient?: Recipient;
	question?: string;
	message?: MessageData;
	blocked?: boolean;
	votes?: number;
	reply?: string;
	qnaId?: string;
	created: string;
}

interface ChatUpdatedProps {
	closeIncomingMessage?: boolean;
	showChatWindow?: boolean;
	messages?: ChatMessage[];
	questions?: ChatMessage[];
	onChatInputFocus?: () => void;
	onKeyUp: (_e: React.KeyboardEvent, _messageText: string, _setMessageText: (_text: string) => void) => void;
	qna?: any;
	poll?: any;
	pollResult?: any;
	messageContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
	settings: any;
	onChatNotificationClick?: () => void;
	onEditProfileClick?: () => void;
	onClose?: () => void;
	disabledChatInput?: boolean;
	onRefreshButtonClick: () => void;
	onUpvoteButtonClick?: (_e: React.MouseEvent, _id: string, _qnaId?: string) => void;
	onSendQuestionClick?: (_event: React.MouseEvent | React.KeyboardEvent, _question: any) => void;
	onFocusOnQuestionField?: () => void;
	isMobile?: boolean;
}

const ChatUpdated: React.FC<ChatUpdatedProps> = ({
	closeIncomingMessage,
	showChatWindow,
	messages: messagesProps,
	questions,
	onChatInputFocus,
	onKeyUp,
	qna,
	poll,
	pollResult,
	messageContainerRef: messageContainerRefProps,
	settings,
	onChatNotificationClick,
	onEditProfileClick,
	onClose,
	disabledChatInput,
	onRefreshButtonClick,
	onUpvoteButtonClick,
	onSendQuestionClick,
	onFocusOnQuestionField,
	isMobile
}) => {
	const { t } = useTranslation();
	const [messageText, setMessageText] = useState('');
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const [showMessageNotification, setShowMessageNotification] = useState(false);
	const [isAllowToRefresh, setAllowToRefresh] = useState(true);

	const chatInputRef = useRef<HTMLInputElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

	const { username } = useChatLogin();
	const { accountId, isChatActivated } = useConfiguration();

	// The presenter/slides layout wraps this component in `.qplayer-presenter__player`,
	// which is given `isolation: isolate` so its stacking order can't leak z-index onto
	// the slide panel next to it. That traps the chat window's z-index too, so it portals
	// out to `.qplayer-app` to sit above the rest of the widget instead.
	useLayoutEffect(() => {
		const isolatedAncestor = chatContainerRef.current?.closest('.qplayer-presenter__player');
		const nextTarget = isolatedAncestor ? ((chatContainerRef.current?.closest('.qplayer-app') as HTMLElement | null) ?? document.body) : null;
		setPortalTarget(nextTarget);
		// Runs once on mount only: once portaled, the ref's DOM position moves out from
		// under `.qplayer-presenter__player`, so recomputing on every render would flip
		// the decision back and forth forever (infinite setState loop).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Portaling escapes the player's own stacking context, but not the host page's: an embed
	// usually sits inside one, and no z-index can lift a descendant above what is outside it -
	// so the chat ended up behind the page's own sections. The top layer is the only way out,
	// and a manual popover is how a non-modal element gets in (the slide fullscreen uses the
	// other way in, <dialog>.showModal(), which is no good here because it makes the player
	// inert). The attribute is set imperatively so a browser without support simply keeps the
	// old behaviour instead of inheriting the UA rule that hides an unopened popover.
	useLayoutEffect(() => {
		const chatWindow = chatContainerRef.current as any;
		if (!portalTarget || !chatWindow || typeof chatWindow.showPopover !== 'function') {
			return;
		}

		chatWindow.setAttribute('popover', 'manual');
		try {
			chatWindow.showPopover();
		} catch {
			chatWindow.removeAttribute('popover');
			return;
		}

		return () => {
			try {
				chatWindow.hidePopover();
			} catch {
				// Already removed from the top layer - nothing to undo.
			}
			chatWindow.removeAttribute('popover');
		};
	}, [portalTarget]);

	// Only as the full-width sheet does the chat cover what is behind it, and only then should
	// that stop scrolling - the top layer changes paint order alone, it holds nothing still. The
	// docked panel on a wider screen leaves the page readable, so the page stays scrollable.
	// Both scrollers are locked: the page, and the panel, which scrolls on its own. The 600px is
	// the sheet's own breakpoint in App.scss.
	useEffect(() => {
		if (!portalTarget) {
			return;
		}
		const sheetWidth = window.matchMedia('(max-width: 600px)');
		const panel = portalTarget.querySelector('.qplayer-presenter') as HTMLElement | null;
		const previousPanelOverflow = panel?.style.overflow ?? '';
		const previousBodyOverflow = document.body.style.overflow;

		const applyScrollLock = () => {
			if (panel) {
				panel.style.overflow = sheetWidth.matches ? 'hidden' : previousPanelOverflow;
			}
			document.body.style.overflow = sheetWidth.matches ? 'hidden' : previousBodyOverflow;
		};

		applyScrollLock();
		sheetWidth.addEventListener('change', applyScrollLock);

		return () => {
			sheetWidth.removeEventListener('change', applyScrollLock);
			if (panel) {
				panel.style.overflow = previousPanelOverflow;
			}
			document.body.style.overflow = previousBodyOverflow;
		};
	}, [portalTarget]);

	const messToShow = useMemo(() => {
		const messages = isChatActivated ? messagesProps ?? [] : [];
		let mess: ChatMessage[] = [...(messages ?? []), ...(questions ?? [])];
		mess = mess.sort((messA, messB) => {
			return new Date(messA.created).getTime() - new Date(messB.created).getTime();
		});
		return mess;
	}, [messagesProps, questions, isChatActivated]);

	const pinnedMessage = useMemo(() => {
		const pinnedMessages = messagesProps?.filter((item) => item.message?.pinned);
		return (pinnedMessages ?? []).pop();
	}, [messagesProps]);

	const messageContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!showMessageNotification) {
			return;
		}

		const showNotification =
			Math.abs(
				(messageContainerRef.current?.scrollHeight ?? 0) -
					(messageContainerRef.current?.scrollTop ?? 0) -
					(messageContainerRef.current?.clientHeight ?? 0)
			) > 100;
		if (showNotification !== showMessageNotification) {
			setShowMessageNotification(showNotification);
		}
	}, [showMessageNotification]);

	useEffect(() => {
		if (!messageContainerRef || !messageContainerRef.current || (messToShow ?? []).length === 0) {
			setShowMessageNotification(false);
			return;
		}

		setShowMessageNotification(
			Math.abs(
				(messageContainerRef.current?.scrollHeight ?? 0) -
					(messageContainerRef.current?.scrollTop ?? 0) -
					(messageContainerRef.current?.clientHeight ?? 0)
			) > 100
		);
	}, [messageContainerRef, messToShow]);

	useEffect(() => {
		if (closeIncomingMessage || disabledChatInput || !chatInputRef?.current) {
			return;
		}
		chatInputRef?.current?.focus();
	}, [disabledChatInput, chatInputRef, closeIncomingMessage]);

	const onNotificationClick = useCallback(() => {
		onChatNotificationClick?.();
		setShowMessageNotification(false);
	}, [onChatNotificationClick]);

	const onRefreshClick = useCallback(() => {
		if (!isAllowToRefresh || closeIncomingMessage) {
			return;
		}

		setAllowToRefresh(false);
		onRefreshButtonClick();
		const allowToRefreshTimeout = setTimeout(() => {
			clearTimeout(allowToRefreshTimeout);
			setAllowToRefresh(true);
		}, 3000);
	}, [closeIncomingMessage, isAllowToRefresh, onRefreshButtonClick]);

	const isVoted = useCallback(
		(id: string) => {
			const upvotedQuestions = Cookies.get(`chat_upvoted_${accountId}`);
			return (upvotedQuestions ?? '').includes(id);
		},
		[accountId]
	);

	const content = (
		<ChatContainer
			ref={chatContainerRef}
			showChatWindow={showChatWindow}
			insidePresenter={!!portalTarget}
			isPortedMobile={isMobile && !!portalTarget}
			className={joinClassNames('chat-container', portalTarget ? 'qplayer-chat--inside-presenter' : '')}
		>
			<ChatTabWrapper>
				<ChatTab>{t(isChatActivated ? 'CHAT' : 'QNA')}</ChatTab>
				<ChatTabAction>
					<IconButton
						onClick={onEditProfileClick}
						title={t('PROFILE_SETTINGS')}
						component={'div'}
						role={'button'}
						tabIndex={0}
					>
						<AccountCircleOutlined htmlColor="#000" />
					</IconButton>
					{!!portalTarget && onClose && (
						<IconButton
							onClick={onClose}
							title={t('CHAT_WINDOW_CLOSE')}
							component={'div'}
							role={'button'}
							tabIndex={0}
						>
							<CloseIcon htmlColor="#000" />
						</IconButton>
					)}
				</ChatTabAction>
			</ChatTabWrapper>

			{!closeIncomingMessage && isChatActivated && (
				<EngagementPanelsStack>
					<Poll poll={poll} pollResult={pollResult} settings={settings} />
					<QuestionPanel
						t={t}
						qna={qna}
						onSendQuestionClick={onSendQuestionClick}
						onFocusOnQuestionField={onFocusOnQuestionField}
						settings={settings}
					/>
				</EngagementPanelsStack>
			)}

			<MessagesContainer
				ref={(ref) => {
					if (messageContainerRefProps) {
						messageContainerRefProps.current = ref;
					}
					messageContainerRef.current = ref;
				}}
				style={isChatActivated ? undefined : { padding: '12px 12px 8px 12px' }}
			>
				{isChatActivated && <StartOfChat>{t('START_OF_CHAT')}</StartOfChat>}
				{[...(messToShow ?? [])].map(({ id, sender: { admin, name }, recipient, question, message, blocked, votes, reply, qnaId }, i) => {
					const replyTo =
						recipient?.replyMessageId || recipient?.replyQuestionId
							? messToShow.find((m) => m.id === recipient?.replyMessageId || m.id === recipient?.replyQuestionId)
							: undefined;

					return (
						!blocked && (
							<Message
								primaryColor={settings?.playerStyling?.primaryColor ?? '#126cfc'}
								key={id + i}
								id={id}
								isAdmin={admin}
								name={name}
								content={qnaId ? (replyTo ? reply : question) : message?.text}
								replyContent={replyTo?.qnaId ? replyTo?.question : replyTo?.message?.text}
								isPinned={false}
								isQuestion={!!(question && !reply)}
								isVoteable={!!qna}
								onUpvoteButtonClick={(e: React.MouseEvent) => onUpvoteButtonClick?.(e, id, qnaId)}
								votes={votes ?? 0}
								isVoted={isVoted(id)}
								messageContainerRef={undefined}
								isContainerExpanded={true}
								notificationComponent={undefined}
							/>
						)
					);
				})}
			</MessagesContainer>
			{showEmojiPicker && isChatActivated && (
				<Picker
					pickerStyle={{
						position: 'absolute',
						bottom: '64px',
						left: '0',
						width: '100%',
					}}
					onEmojiClick={(_e: any, emojiObject: any) => {
						setMessageText((prev) => prev + emojiObject.emoji);
						setShowEmojiPicker(false);
					}}
					disableSearchBar={true}
					disableAutoFocus={true}
					disableSkinTonePicker={true}
					preload={true}
				/>
			)}

			{isChatActivated && (
				<Message
					primaryColor={settings?.playerStyling?.primaryColor ?? '#126cfc'}
					name={pinnedMessage?.sender.name ?? ""}
					content={pinnedMessage?.message?.text}
					replyContent={
						pinnedMessage?.recipient?.replyMessageId &&
						messagesProps?.find((x) => x.id === pinnedMessage?.recipient?.replyMessageId)?.message?.text
					}
					isPinned={true}
					isAdmin={!!pinnedMessage?.sender.admin}
					id={pinnedMessage?.id ?? ''}
					votes={0}
					isVoted={false}
					messageContainerRef={undefined}
					isContainerExpanded={true}
					notificationComponent={() =>
						showMessageNotification && (
							<MessageNotification>
								<MessageNotificationContent onClick={onNotificationClick}>
									New message <DownIcon />
								</MessageNotificationContent>
							</MessageNotification>
						)
					}
				/>
			)}
			{isChatActivated && (
				<div>
					<ChatNotice>{messageText.trim().length}/300 {t('LABEL_CHARACTERS')}</ChatNotice>
					<MessageInputContainer>
						<MessageInput
							ref={chatInputRef}
							tabIndex={0}
							placeholder={username ? `${t('SEND_MESSAGE_AS')}${username}` : t('WRITE_MESSAGE')}
							contentEditable={"plaintext-only" as any}
							onClick={onChatInputFocus}
							onKeyUp={(e: any) => {
								onKeyUp(e, messageText, setMessageText);
							}}
							value={messageText}
							onInput={(e: any) => {
								onChatInputFocus?.();
								setMessageText(e.target.value.slice(0, 300));
							}}
							disabled={disabledChatInput || closeIncomingMessage}
						/>
						<EmojiIconContainer
							closeIncomingMessage={closeIncomingMessage}
							onClick={() => !closeIncomingMessage && setShowEmojiPicker(!showEmojiPicker)}
						>
							<InsertEmoticonOutlined style={{ width: '18px', height: '18px' }} />
						</EmojiIconContainer>

						<IconButton
							onClick={onRefreshClick}
							disabled={!isAllowToRefresh || closeIncomingMessage}
							style={{
								position: 'absolute',
								right: '0px',
								zIndex: 1,
							}}
							title={t('REFRESH_CHAT')}
							component={'div'}
							role={'button'}
							tabIndex={0}
						>
							<RefreshIcon
								htmlColor={isAllowToRefresh && !closeIncomingMessage ? '#000' : undefined}
								style={{ width: '18px', height: '18px' }}
							/>
						</IconButton>
					</MessageInputContainer>
				</div>
			)}

			{!closeIncomingMessage && !isChatActivated && (
				<EngagementPanelsStack style={{ backgroundColor: 'rgba(247, 248, 250, 1)', border: '1px solid rgba(229, 233, 236, 1)', maxHeight: "calc(100% - 45px)" }}>
					<Poll poll={poll} pollResult={pollResult} settings={settings} />
					<QuestionPanel
						t={t}
						qna={qna}
						onSendQuestionClick={onSendQuestionClick}
						onFocusOnQuestionField={onFocusOnQuestionField}
						settings={settings}
					/>
				</EngagementPanelsStack>
			)}

			<ChatLoginOverlay settings={settings} />
		</ChatContainer>
	);

	return portalTarget ? ReactDOM.createPortal(content, portalTarget) : content;
};

export default ChatUpdated;
